import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Radio, Space, Tabs, Tag, Card, Spin } from "antd";
import { EnvironmentOutlined, DownOutlined } from "@ant-design/icons";
import { CreatorProfile as ICreatorProfile } from "interfaces/creator-profile";
import "./CreatorProfile.scss";
import { Creator, FoodiePost, Social } from "interfaces/creator";
import { useLoaderData, useNavigate, useNavigation } from "react-router-dom";
import { openNewTabSafe } from "helpers/url-helpers";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { ReactComponent as HeartIcon } from "assets/images/heart-icon.svg";
import { ReactComponent as EyeIcon } from "assets/images/eye-icon.svg";
import { ReactComponent as MessagesIcon } from "assets/images/message-text-icon.svg";
import { ReactComponent as FlashIcon } from "assets/images/flash-icon.svg";
import { ReactComponent as RepeatIcon } from "assets/images/repeat-icon.svg";
import {
  formatNumberKMBT,
  ImgWithErrorHandling,
} from "../influencers/InfluencerCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCartShopping,
  faChartLine,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleUser,
  faCutlery,
  faExclamationCircle,
  faEye,
  faHeart,
  faMoneyBill1Wave,
  faPaperPlane,
  faPlay,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { AvatarWithErrorHandling } from "pages/collabs/Collabs";
import { AWS_S3_URL } from "helpers/aws-s3-url";
import dayjs from "dayjs";
import PendingActivities from "components/PendingActivities/PendingActivities";
import { formatUSD } from "helpers/money-format";
import { performanceScoreColors } from "pages/home/NewHome";
import { favoriteCreator, unfavoriteCreator } from "services/SauceService";
import { BusinessState, getFavoritedCreators } from "redux-context/business";
import { useDispatch, useSelector } from "react-redux";
import { AnimateHeartIcon } from "../influencers/CreatorCard";

const { TabPane } = Tabs;

interface CreatorProfileProps {
  creatorId?: string;
  data?: ICreatorProfile;
}

interface CollabVenue {
  addressCoordinates: {
    coordinates: [number, number];
    type: string;
  };
  address: string;
  name: string;
  uuid: string;
  city: string;
  cityState: string;
}

interface Collab {
  collabId: string;
  venue: CollabVenue;
  startDate: string;
  endDate: string;
  reach: number;
  likes: number;
  comments: number;
  reshares: number;
  engagementRate: string;
  dinners: number;
  reachTotal: number;
  postCoverImg: string;
  socialMediaUrl: string;
}

export interface CreatorData {
  foodie: Creator;
  foodiePosts: FoodiePost[];
  collabs: Collab[];
}

const CreatorProfile: React.FC<CreatorProfileProps> = ({ data }) => {
  const creatorData = useLoaderData() as CreatorData;
  const navigation = useNavigation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { business, favoritedCreators } = useSelector(
    (state: BusinessState) => state.business,
  );
  const loading = navigation.state === "loading";
  const postsContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>("instagram");
  const [showPerformanceDetails, setShowPerformanceDetails] =
    useState<boolean>(false);
  const [showCampaignModal, setShowCampaignModal] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("Recent");
  const [liked, setLiked] = useState<boolean>(false);
  const [heartAnimating, setHeartAnimating] = useState<boolean>(false);

  const creator: Creator = useMemo(
    () => creatorData?.foodie || ({} as Creator),
    [creatorData],
  );

  const hasInstagram =
    !!creator?.instagram && (Number(creator.instagram.followersCount) || 0) > 0;
  const hasTikTok =
    !!creator?.tiktok && (Number(creator.tiktok.followersCount) || 0) > 0;

  const showMonetaryComp =
    creatorData?.foodie?.engagementCPMPriceMin &&
    creatorData?.foodie?.engagementCPMPriceMin > 20 &&
    creatorData?.foodie?.engagementCPMPriceMax &&
    creatorData?.foodie?.engagementCPMPriceMax > 20;

  const currentStats = useMemo<Social>(() => {
    const fallbackTab = hasInstagram
      ? "instagram"
      : hasTikTok
        ? "tiktok"
        : "instagram";
    const tab =
      activeTab && activeTab !== ""
        ? (activeTab as keyof Creator | string)
        : fallbackTab;
    const stats = (creator as any)[tab];
    return (stats as Social) || (creator.instagram as Social) || ({} as Social);
  }, [activeTab, creator, hasInstagram, hasTikTok]);

  const mockCampaigns = [
    {
      id: "campaign-1",
      name: "Summer Menu Launch",
      status: "Active",
      creators: 5,
      budget: 50000,
    },
    {
      id: "campaign-2",
      name: "Fall Harvest Collection",
      status: "Planning",
      creators: 0,
      budget: 75000,
    },
    {
      id: "campaign-3",
      name: "Holiday Special Events",
      status: "Draft",
      creators: 0,
      budget: 100000,
    },
  ];

  const sortedPosts = useMemo(() => {
    if (!creatorData?.foodiePosts) return [];

    const posts = [...creatorData?.foodiePosts];

    switch (sortBy) {
      case "performance":
        return posts.sort((a, b) => {
          // Sort by a combination of engagement rate and reach
          const aScore = (a.engagementRate || 0) * 0.6 + (a.reach || 0) * 0.4;
          const bScore = (b.engagementRate || 0) * 0.6 + (b.reach || 0) * 0.4;
          return bScore - aScore;
        });
      case "engagement":
        return posts.sort(
          (a, b) => (b.engagementRate || 0) - (a.engagementRate || 0),
        );
      case "reach":
        return posts.sort((a, b) => (b.reach || 0) - (a.reach || 0));
      case "intent":
        return posts.sort((a, b) => (b.dinner || 0) - (a.dinner || 0));
      case "latest":
      default:
        return posts; // Keep original order for latest
    }
  }, [creatorData?.foodiePosts, sortBy]);

  const scrollPosts = (direction: "left" | "right") => {
    if (postsContainerRef.current) {
      const scrollAmount = 300; // Adjust this value as needed
      const currentScroll = postsContainerRef.current.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      postsContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  };

  const handleHeartClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setHeartAnimating(true);
    const params = {
      businessId: business._id,
      creatorId: creator.foodieId,
    };
    try {
      if (liked) {
        await unfavoriteCreator(params);
      } else {
        await favoriteCreator(params);
      }
      dispatch(getFavoritedCreators());
    } catch (error) {
      console.error("Error favoriting creator:", error);
    }
    setLiked((prev) => !prev);
    setHeartAnimating(false);
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(event.target.value);
  };

  const onClickOpenPost = (url: string) => {
    if (!url) return;
    openNewTabSafe(url);
  };

  const handleTabChange = (key: string) => setActiveTab(key);
  const handleAddToCampaign = () => setShowCampaignModal(true);
  const handleConfirmCampaign = () => {
    if (selectedCampaign) {
      // placeholder behavior
      console.log("Adding creator to campaign:", selectedCampaign);
      setShowCampaignModal(false);
      setSelectedCampaign("");
    }
  };

  // Lista que reduz repetição na renderização dos cartões de estatísticas
  const statItems = [
    {
      show: (currentStats?.followersCount || 0) > 0,
      icon: faCircleUser,
      value: formatNumberKMBT(currentStats?.followersCount, 0) || "-",
      label: "Followers",
    },
    {
      show: (currentStats?.engagementRate || 0) > 0,
      icon: faBolt,
      value: `${currentStats?.engagementRate || "-"}%`,
      label: "Engagement",
    },
    {
      show: (currentStats?.averageLikes || 0) > 0,
      icon: faHeart,
      value: formatNumberKMBT(currentStats?.averageLikes || 0) || "-",
      label: "AVG likes",
    },
    {
      show: (currentStats?.averageViews || 0) > 0,
      icon: faEye,
      value: formatNumberKMBT(currentStats?.averageViews || 0) || "-",
      label: "AVG views",
    },
    {
      show: (currentStats?.dinnerIntentByPosts || 0) > 0,
      icon: faCartShopping,
      value: currentStats?.dinnerIntentByPosts || "-",
      label: "Average Purchase Intent",
      fullWidth: true,
    },
  ];

  useEffect(() => {
    const isLiked = favoritedCreators?.some(
      (fav) => fav.creatorId === creator.foodieId,
    );
    setLiked(isLiked);
  }, [favoritedCreators, creator.foodieId]);

  useEffect(() => {
    dispatch(getFavoritedCreators());
  }, [dispatch]);

  useEffect(() => {
    const inst = creatorData?.foodie?.instagram;
    const tik = creatorData?.foodie?.tiktok;

    const toNumber = (v: any) => Number(v) || 0;
    const instaFollowers = toNumber(inst?.followersCount);
    const tiktokFollowers = toNumber(tik?.followersCount);
    const instaEng = toNumber(inst?.engagementRate);
    const tiktokEng = toNumber(tik?.engagementRate);

    const hasInst = !!inst && (instaFollowers > 0 || instaEng > 0);
    const hasTik = !!tik && (tiktokFollowers > 0 || tiktokEng > 0);

    if (hasInst && !hasTik) {
      setActiveTab("instagram");
    } else if (hasTik && !hasInst) {
      setActiveTab("tiktok");
    } else if (hasInst && hasTik) {
      setActiveTab(tiktokFollowers > instaFollowers ? "tiktok" : "instagram");
    } else {
      // no social data available
      setActiveTab("");
    }
  }, [creatorData]);

  const renderCompSection = () => {
    if (showMonetaryComp) {
      return (
        <>
          <div
            className="comp-label"
            style={{
              fontSize: 14,
              color: "#374151",
              fontWeight: 600,
              textAlign: "center",
              marginBottom: 12,
            }}
          >
            Minimum Recommended Monetary Comp
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 18,
              maxWidth: 420,
              margin: "0 auto",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#F97316",
                }}
              >
                <FontAwesomeIcon icon={faMoneyBill1Wave} />{" "}
                {`${formatUSD(
                  creatorData?.foodie?.engagementCPMPriceMin,
                  false,
                )}`}
              </div>
            </div>
          </div>
        </>
      );
    }

    // fallback: product comp
    return (
      <>
        <div
          className="comp-label"
          style={{
            fontSize: 14,
            color: "#374151",
            fontWeight: 600,
            textAlign: "center",
            marginBottom: 12,
          }}
        >
          Recommended Product Comp
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 18,
            maxWidth: 420,
            margin: "0 auto",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: "#F97316",
              }}
            >
              <FontAwesomeIcon icon={faCutlery} /> {`${formatUSD(75, false)}`}
            </div>
          </div>
        </div>
      </>
    );
  };

  if (loading) return <Spin />;

  return (
    <div className="creator-profile">
      <div className="profile-header">
        <div className="profile-content">
          <div className="creator-avatar">
            <img src={creator?.avatarUrl} alt="Creator profile" />
          </div>

          <div className="creator-info">
            <div className="creator-details">
              <div className="basic-info">
                <h1>
                  {creator?.name}{" "}
                  <button
                    className={`creator-card__heart${liked ? " is-liked" : ""}${
                      heartAnimating ? " is-animating" : ""
                    }`}
                    onClick={handleHeartClick}
                    aria-label={liked ? "Desfavoritar" : "Favoritar"}
                  >
                    <AnimateHeartIcon filled={liked} />
                  </button>
                </h1>
                <div className="location">
                  <EnvironmentOutlined />
                  <span>{creator?.locations?.join(" ∘ ")} </span>
                </div>

                <p className="bio">{creator?.bio}</p>

                <div>
                  <h3
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
                  >
                    Specialties
                  </h3>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {creator.categories?.map((category, index) => (
                      <Tag key={index}>{category}</Tag>
                    ))}
                  </div>
                </div>
                <div>
                  <h3
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
                  >
                    Dietary Preferences
                  </h3>
                  {creator.dietaryPreferences?.map((diet, index) => (
                    <Tag key={index}>{diet}</Tag>
                  ))}
                </div>
              </div>

              <div className="profile-actions">
                <div className="platform-tabs">
                  {hasInstagram || hasTikTok ? (
                    <Tabs
                      activeKey={
                        activeTab ||
                        (hasInstagram ? "instagram" : hasTikTok ? "tiktok" : "")
                      }
                      onChange={handleTabChange}
                      size="small"
                    >
                      {hasInstagram && (
                        <TabPane
                          tab={
                            <span style={{ color: "#E1306C" }}>Instagram</span>
                          }
                          key="instagram"
                        />
                      )}

                      {hasTikTok && (
                        <TabPane
                          tab={<span style={{ color: "#000" }}>TikTok</span>}
                          key="tiktok"
                        />
                      )}
                    </Tabs>
                  ) : null}
                </div>

                <div className="stats-grid">
                  {statItems.map((s, idx) =>
                    s.show ? (
                      <div
                        className={`stat-card ${s.fullWidth ? "full-width" : ""}`}
                        key={`${s.label}_${idx}`}
                      >
                        <FontAwesomeIcon icon={s.icon as any} color="#f6a623" />
                        <div className="stat-value">{s.value}</div>
                        <div className="stat-label">{s.label}</div>
                      </div>
                    ) : null,
                  )}

                  <div className="stat-card full-width">
                    <div
                      className="perf-score-card"
                      onClick={() =>
                        setShowPerformanceDetails(!showPerformanceDetails)
                      }
                    >
                      <FontAwesomeIcon icon={faChartLine} color="#f6a623" />
                      <div className="perf-left">
                        <div className="perf-percent">
                          {creatorData?.foodie?.commitmentRate?.toFixed(0)}%
                        </div>
                        <div className="perf-label">Performance Score</div>
                      </div>

                      <div className="perf-right">
                        <div className="perf-progress" aria-hidden>
                          <div
                            className="perf-progress-fill"
                            style={{
                              width: `${creatorData?.foodie?.commitmentRate}%`,
                              backgroundColor: performanceScoreColors(
                                creatorData?.foodie?.commitmentRate || 0,
                              ),
                            }}
                          />
                        </div>
                        <DownOutlined
                          className={showPerformanceDetails ? "rotated" : ""}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {showPerformanceDetails && (
              <div className="performance-details">
                <div className="perf-header">
                  <h4>Performance Factors</h4>
                </div>

                <div className="perf-columns">
                  <div className="perf-column">
                    <div className="perf-title positive">
                      <span className="badge">
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          style={{ fontSize: 14 }}
                        />
                      </span>
                      <strong>Positive Contributions</strong>
                    </div>

                    <ul className="perf-list">
                      <li className="perf-item">
                        <div className="perf-desc">Collabs engaged </div>
                        <div className="perf-value positive">
                          {creatorData?.foodie?.goodFactors?.collabEngagement}
                        </div>
                      </li>
                      <li className="perf-item">
                        <div className="perf-desc">
                          Content upload on time (within 5 days){" "}
                        </div>
                        <div className="perf-value positive">
                          {creatorData?.foodie?.goodFactors?.uploadOnTime}
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="perf-column">
                    <div className="perf-title negative">
                      <span className="badge">
                        <FontAwesomeIcon icon={faExclamationCircle} />
                      </span>
                      <strong>Negative Contributions</strong>
                    </div>

                    <ul className="perf-list">
                      <li className="perf-item">
                        <div className="perf-desc">Cancelations </div>
                        <div className="perf-value negative">
                          {creatorData?.foodie?.badFactors?.collabCancellation}
                        </div>
                      </li>
                      <li className="perf-item">
                        <div className="perf-desc">No-shows </div>
                        <div className="perf-value negative">
                          {creatorData?.foodie?.badFactors?.collabNoShow}
                        </div>
                      </li>
                      <li className="perf-item">
                        <div className="perf-desc">Reschedules </div>
                        <div className="perf-value negative">
                          {creatorData?.foodie?.badFactors?.collabRescheduling}
                        </div>
                      </li>
                      <li className="perf-item">
                        <div className="perf-desc">No content uploaded </div>
                        <div className="perf-value negative">
                          {creatorData?.foodie?.badFactors?.missingUpload}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="perf-divider" />

                <div className="overall-score">
                  <span className="overall-label">Overall Score</span>
                  <span className="overall-number">
                    {creatorData?.foodie?.commitmentRate?.toFixed(0)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div
          className="bottom-actions"
          style={{ flexDirection: "row", gap: 24 }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              gap: 24,
            }}
          >
            <div>{renderCompSection()}</div>
            {showMonetaryComp &&
            (creatorData?.foodie?.cpmImpressionCost || 0) > 0 ? (
              <>
                <div
                  style={{
                    height: 64,
                    width: 1,
                    backgroundColor: "#D1D5DB",
                    margin: "0 8px",
                  }}
                />
                <div>
                  <div
                    className="comp-label"
                    style={{
                      fontSize: 14,
                      color: "#374151",
                      fontWeight: 600,
                      textAlign: "center",
                      marginBottom: 12,
                    }}
                  >
                    Calculated CPM
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 18,
                      maxWidth: 420,
                      margin: "0 auto",
                    }}
                  >
                    <div
                      style={{ textAlign: "center", display: "flex", gap: 8 }}
                    >
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          color: "#F97316",
                        }}
                      >
                        {`${formatUSD(creatorData?.foodie?.cpmImpressionCost, true)}`}
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 500,
                          alignItems: "center",
                          display: "flex",
                          color: "#6B7280",
                        }}
                      >
                        {" "}
                        / 1000 views
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          {creator?.inviteAvailable && (
            <div
              className="action-buttons"
              style={{ flexDirection: "column", gap: 8 }}
            >
              <Button
                type="primary"
                onClick={() => navigate(`/creators/${creator.foodieId}/invite`)}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Send Invite
              </Button>
              <Button onClick={handleAddToCampaign} style={{ display: "none" }}>
                <FontAwesomeIcon icon={faPlus} />
                Add to Campaign
              </Button>
            </div>
          )}
        </div>
      </div>

      {sortedPosts.length > 0 && (
        <div className="portfolio-section">
          <div className="portfolio-header">
            <div className="section-title">
              <h2>Last notable content ({sortedPosts.length})</h2>
              {/* <DownOutlined /> */}
            </div>
            <div className="content-controls" style={{ display: "none" }}>
              <div className="sort-select">
                <span>Sort by:</span>
                <select value={sortBy} onChange={handleSortChange}>
                  <option value="latest">Latest</option>
                  <option value="performance">Performance</option>
                  <option value="engagement">Engagement</option>
                  <option value="reach">Reach</option>
                  <option value="intent">Purchase Intent</option>
                </select>
              </div>
            </div>
          </div>

          <div className="portfolio-content">
            <div className="posts-container">
              <div className="posts-scroll-controls">
                <button
                  className="scroll-btn scroll-btn--left"
                  onClick={() => scrollPosts("left")}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  className="scroll-btn scroll-btn--right"
                  onClick={() => scrollPosts("right")}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
              <div className="posts-grid" ref={postsContainerRef}>
                {sortedPosts.map((post, index) => (
                  <div
                    className="post-card"
                    key={`POST_${index}_${post.socialMediaUrl}`}
                    onClick={() => onClickOpenPost(post.socialMediaUrl)}
                  >
                    <div className="post-image-container">
                      <ImgWithErrorHandling
                        src={post.coverImg}
                        alt={post.socialMediaName}
                        className="post-image"
                      />
                      <div className="post-platform">
                        {post.socialMediaName?.toLowerCase() === "tiktok" ? (
                          <TikTokIcon stroke="#fff" />
                        ) : (
                          <InstagramIcon fill="#fff" />
                        )}
                      </div>
                    </div>
                    <div className="post-details">
                      <div className="post-header">
                        <span className="post-date">
                          {dayjs(post.createdAt).format("MMM D, YYYY")}
                        </span>
                      </div>
                      <div className="post-metrics">
                        <div className="metric-item">
                          <FontAwesomeIcon icon={faBolt} color="#f6a623" />
                          <span>{post.engagementRate || "-"}% Eng</span>
                        </div>
                        <div className="metric-item">
                          <FontAwesomeIcon icon={faPlay} />
                          <span>
                            {formatNumberKMBT(post.reach) || "-"} Views
                          </span>
                        </div>
                        <div className="metric-item">
                          <FontAwesomeIcon icon={faHeart} color="#ef4444" />
                          <span>
                            {formatNumberKMBT(post.likes) || "-"} Likes
                          </span>
                        </div>
                        <div className="metric-item">
                          <FontAwesomeIcon
                            icon={faCartShopping}
                            color="#52c41a"
                          />
                          <span>{post.dinner || "-"} Intent</span>
                        </div>
                      </div>
                      <div className="performance-score">
                        <div className="score-label">Performance Score</div>
                        <div className="score-bar">
                          <div
                            className="score-progress"
                            style={{
                              width: `${post.performanceScore || 0}%`,
                              backgroundColor: performanceScoreColors(
                                post.performanceScore || 0,
                              ),
                            }}
                          ></div>
                          <span
                            className="score-value"
                            style={{
                              color: performanceScoreColors(
                                post.performanceScore || 0,
                              ),
                            }}
                          >
                            {post.performanceScore || "-"}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pending-activities">
        <h2 className="pa-title">Pending Activities</h2>
        <PendingActivities creator={creator} />
      </div>

      {creatorData?.collabs?.length > 0 && false && (
        <div className="collaboration-history">
          <h2>Recent Collaborations</h2>

          <div className="collaborations-card">
            <div className="collaboration-list">
              {creatorData?.collabs.map((collab, index) => (
                <Card key={collab.collabId} style={{ borderRadius: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <div className="calendar">
                        <span className="month">
                          {dayjs.utc(collab.startDate || "").format("MMM")}{" "}
                          {" ∘ "}
                          {dayjs.utc(collab.endDate || "").format("MMM")}
                        </span>
                        <span className="day">
                          {dayjs.utc(collab.endDate || "").format("DD")}
                        </span>
                      </div>
                      <AvatarWithErrorHandling
                        src={`${AWS_S3_URL}${collab?.venue?.uuid}_cover.png`}
                        shape="round"
                        size={60}
                        style={{ position: "relative", marginLeft: -16 }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        height: "100%",
                        flex: 1,
                      }}
                    >
                      <span style={{ fontSize: 16, fontWeight: 500 }}>
                        {collab?.venue?.name}
                      </span>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          color: "#6b7280",
                          fontSize: 13,
                          marginTop: 6,
                        }}
                      >
                        <span>
                          {collab?.venue?.cityState ||
                            collab?.venue?.city ||
                            collab?.venue?.address}
                        </span>
                      </div>
                    </div>

                    <div className="content" style={{ flex: 1 }}>
                      <span className="info">
                        <EyeIcon />
                        {collab?.reach || "-"}
                      </span>
                      <span className="info">
                        <HeartIcon />
                        {collab?.likes || "-"}
                      </span>
                      <span className="info">
                        <MessagesIcon />
                        {collab?.comments || "-"}
                      </span>
                      <span className="info">
                        <RepeatIcon />
                        {collab?.reshares || "-"}
                      </span>
                      <span className="info">
                        <FlashIcon />
                        {collab?.engagementRate || "-"}%
                      </span>
                    </div>

                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          justifyContent: "flex-end",
                        }}
                      >
                        <div key={index}>
                          <ImgWithErrorHandling
                            src={collab?.postCoverImg}
                            alt=""
                            className="image-post"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              onClickOpenPost(collab?.socialMediaUrl)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Add to Campaign"
        open={showCampaignModal}
        onOk={handleConfirmCampaign}
        onCancel={() => {
          setShowCampaignModal(false);
          setSelectedCampaign("");
        }}
        okText="Add to Campaign"
        cancelText="Cancel"
        centered
        width={520}
        okButtonProps={{
          style: {
            background: "#f6a623",
            borderColor: "#f6a623",
            color: "#fff",
            fontWeight: 700,
          },
          disabled: !selectedCampaign,
        }}
        cancelButtonProps={{
          style: { background: "#fff", borderColor: "#e6e6e6", color: "#111" },
        }}
      >
        <div className="campaign-selection">
          <p style={{ marginBottom: 12 }}>Select Campaign:</p>
          <Radio.Group
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            style={{ width: "100%" }}
          >
            <Space
              direction="vertical"
              style={{
                width: "100%",
              }}
            >
              {mockCampaigns.map((c) => {
                const shortBudget = (() => {
                  if (!c.budget) return "—";
                  const k = Math.round(c.budget / 1000);
                  return `$${k}K`;
                })();

                return (
                  <Radio
                    key={c.id}
                    value={c.id}
                    style={{
                      width: "100%",
                      border: "1px solid #d9d9d9",
                      borderRadius: 12,
                      padding: 12,
                    }}
                  >
                    <div
                      className="campaign-option"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        className="campaign-name"
                        style={{ fontWeight: 700, marginBottom: 6 }}
                      >
                        {c.name}
                      </div>
                      <div
                        className="campaign-details"
                        style={{ color: "#6b7280", fontSize: 13 }}
                      >
                        {c.status} • {c.creators} creators • {shortBudget}{" "}
                        budget
                      </div>
                    </div>
                  </Radio>
                );
              })}
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    </div>
  );
};

export default CreatorProfile;
